import { Navigate, useLocation, useParams } from "react-router";
import { OrganizationRouteGuard } from "@/features/auth/components/OrganizationRouteGuard";
import type { RouteConfig } from "@/router/types";
import { lazyWithRetry } from "@/shared/utils/lazyWithRetry";

const LegacyRedirect = ({ to }: { to: (params: Record<string, string | undefined>) => string }) => {
  const params = useParams();
  const { search } = useLocation();
  return <Navigate to={{ pathname: to(params), search }} replace />;
};

// `<Navigate to="/chemin" />` remplace TOUTE la location, query comprise :
// le retour Paybox (`?cart_id=…&state=success`) arrivait sur /locations/
// confirmation et repartait vers /catalogue/confirmation nu - l'écran
// affichait « Paramètres invalides » après un paiement réussi (ANO-24).
const RedirectKeepingSearch = ({ to }: { to: string }) => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} replace />;
};

export const RENTALS_PATH = "/catalogue";
export const RENTALS_RESERVATIONS_PATH = "/catalogue/reservations";
export const RENTALS_CONFIRMATION_PATH = "/catalogue/confirmation";
export const RENTALS_CHECKOUT_PATH = "/catalogue/checkout";
export const RENTALS_ACCOUNT_STATEMENT_PATH = "/catalogue/mes-comptes";

const LEGACY_RENTALS_PATH = "/locations";
const LEGACY_RENTALS_RESERVATIONS_PATH = "/locations/reservations";
const LEGACY_RENTALS_CONFIRMATION_PATH = "/locations/confirmation";
const LEGACY_RENTALS_CHECKOUT_PATH = "/locations/checkout";
const LEGACY_RENTALS_ACCOUNT_STATEMENT_PATH = "/locations/mes-comptes";
// Canonical landing page for the rental bookings list — they live as a
// tab inside /boutique/commandes alongside the shop and deals orders.
export const RENTALS_RESERVATIONS_REDIRECT = "/boutique/commandes?orderType=rentals";

const RentalsListPage = lazyWithRetry(
  () => import("@/features/rentals/pages/RentalsListPage"),
);
const RentalDetailPage = lazyWithRetry(
  () => import("@/features/rentals/pages/RentalDetailPage"),
);
const RentalBookingDetailPage = lazyWithRetry(
  () => import("@/features/rentals/pages/RentalBookingDetailPage"),
);
const RentalBookingConfirmationPage = lazyWithRetry(
  () => import("@/features/rentals/pages/RentalBookingConfirmationPage"),
);
const RentalCheckoutPage = lazyWithRetry(
  () => import("@/features/rentals/pages/RentalCheckoutPage"),
);
// Phase 2 — Checkout cart-level qui pilote N bookings via le cart
// endpoint backend. Sans `:bookingId` → toute la page lit le cart
// courant du bénéf depuis le UnifiedCartContext.
const RentalCartCheckoutPage = lazyWithRetry(
  () => import("@/features/rentals/pages/RentalCartCheckoutPage"),
);
const RentalAccountStatementPage = lazyWithRetry(
  () => import("@/features/rentals/pages/RentalAccountStatementPage"),
);

/**
 * Toutes les routes rentals sont gated par le feature flag
 * `service_catalog` (visibles uniquement pour les orgs qui ont activé
 * le module Catalog côté plan/admin). Le `OrganizationRouteGuard`
 * affiche `AccessDeniedPage` quand le feature est masqué.
 */
const withCatalogGuard = (element: React.ReactNode) => (
  <OrganizationRouteGuard hiddenFeature="service_catalog">
    {element}
  </OrganizationRouteGuard>
);

const rentalsRoutes: RouteConfig[] = [
  {
    path: RENTALS_PATH,
    element: withCatalogGuard(<RentalsListPage />),
    isProtected: true,
    useMainLayout: true,
  },
  // Confirmation page must come before `:itemId` to avoid being shadowed.
  {
    path: RENTALS_CONFIRMATION_PATH,
    element: withCatalogGuard(<RentalBookingConfirmationPage />),
    isProtected: true,
    useMainLayout: true,
  },
  // Legacy /locations/reservations now redirects to the unified Mes
  // commandes page — keeps the URL alive for old links/bookmarks.
  {
    path: RENTALS_RESERVATIONS_PATH,
    element: withCatalogGuard(<Navigate to={RENTALS_RESERVATIONS_REDIRECT} replace />),
    isProtected: true,
    useMainLayout: true,
  },
  {
    path: `${RENTALS_RESERVATIONS_PATH}/:bookingId`,
    element: withCatalogGuard(<RentalBookingDetailPage />),
    isProtected: true,
    useMainLayout: true,
  },
  {
    path: `${RENTALS_CHECKOUT_PATH}/:bookingId`,
    element: withCatalogGuard(<RentalCheckoutPage />),
    isProtected: true,
    useMainLayout: true,
  },
  // Phase 2 — Checkout cart-level (N bookings, Paybox session unique
  // ou pay-later d'un coup). Sans `:bookingId` → on lit le cart
  // courant via le UnifiedCartContext.
  {
    path: RENTALS_CHECKOUT_PATH,
    element: withCatalogGuard(<RentalCartCheckoutPage />),
    isProtected: true,
    useMainLayout: true,
  },
  {
    path: RENTALS_ACCOUNT_STATEMENT_PATH,
    element: withCatalogGuard(<RentalAccountStatementPage />),
    isProtected: true,
    useMainLayout: true,
  },
  {
    path: `${RENTALS_PATH}/:itemId`,
    element: withCatalogGuard(<RentalDetailPage />),
    isProtected: true,
    useMainLayout: true,
  },
  // Legacy /locations* routes - redirigent désormais vers leur
  // équivalent /catalogue* pour aligner la barre d'URL. Les query
  // params (cart_id, booking_id, state Paybox…) doivent être portés
  // explicitement : `<Navigate replace />` les jette (ANO-24).
  {
    path: LEGACY_RENTALS_PATH,
    element: <RedirectKeepingSearch to={RENTALS_PATH} />,
    isProtected: true,
    useMainLayout: true,
  },
  {
    path: LEGACY_RENTALS_CONFIRMATION_PATH,
    element: <RedirectKeepingSearch to={RENTALS_CONFIRMATION_PATH} />,
    isProtected: true,
    useMainLayout: true,
  },
  {
    path: LEGACY_RENTALS_RESERVATIONS_PATH,
    element: <Navigate to={RENTALS_RESERVATIONS_REDIRECT} replace />,
    isProtected: true,
    useMainLayout: true,
  },
  {
    path: `${LEGACY_RENTALS_RESERVATIONS_PATH}/:bookingId`,
    element: (
      <LegacyRedirect
        to={(p) => `${RENTALS_RESERVATIONS_PATH}/${p.bookingId ?? ""}`}
      />
    ),
    isProtected: true,
    useMainLayout: true,
  },
  {
    path: `${LEGACY_RENTALS_CHECKOUT_PATH}/:bookingId`,
    element: (
      <LegacyRedirect
        to={(p) => `${RENTALS_CHECKOUT_PATH}/${p.bookingId ?? ""}`}
      />
    ),
    isProtected: true,
    useMainLayout: true,
  },
  {
    path: LEGACY_RENTALS_CHECKOUT_PATH,
    element: <RedirectKeepingSearch to={RENTALS_CHECKOUT_PATH} />,
    isProtected: true,
    useMainLayout: true,
  },
  {
    path: LEGACY_RENTALS_ACCOUNT_STATEMENT_PATH,
    element: <RedirectKeepingSearch to={RENTALS_ACCOUNT_STATEMENT_PATH} />,
    isProtected: true,
    useMainLayout: true,
  },
  {
    path: `${LEGACY_RENTALS_PATH}/:itemId`,
    element: (
      <LegacyRedirect
        to={(p) => `${RENTALS_PATH}/${p.itemId ?? ""}`}
      />
    ),
    isProtected: true,
    useMainLayout: true,
  },
];

export default rentalsRoutes;
